import Popover from '@liveauctioneers/caterwaul-components/lib/Popover/Popover';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Bell } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import { NotificationPermissions } from '@/enums/notificationPermissions.enum';
import { AlertTypes } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { openSMSConfirmationModal } from '@/redux/modules/modal';
import { getPermission, requestBrowserPermission } from '@/redux/modules/notification';
import { getSubscribedToBrowserAlerts, getSubscribedToSMSAlerts } from '@/redux/modules/smsAlerts';

type Props = {
    className?: string;
    hideManage?: boolean;
    id?: string;
    messageId: string;
    noSms?: boolean;
    to: string;
    withPopover?: boolean;
};

const ManageButton = ({
    className = '',
    hideManage = false,
    id,
    messageId,
    noSms = false,
    to,
    withPopover = false,
}: Props) => {
    const dispatch = useAppDispatch();
    const hasSMSalerts = useAppSelector(getSubscribedToSMSAlerts);
    const hasBrowserAlerts = useAppSelector(getSubscribedToBrowserAlerts);
    const permission = useAppSelector(getPermission);

    const onClick = React.useCallback(() => {
        dispatch(openSMSConfirmationModal(AlertTypes.All));
        if (permission !== NotificationPermissions.Denied) {
            dispatch(requestBrowserPermission());
        }
    }, [dispatch, permission]);

    // TODO: replace this popover with tooltip

    return hasSMSalerts || hasBrowserAlerts || noSms ? (
        hideManage ? null : (
            <Link href={to}>
                <InlineButton
                    icon={Bell}
                    id={id || messageId}
                    size="md"
                >
                    <FormattedMessage id={messageId} />
                </InlineButton>
            </Link>
        )
    ) : (
        <StyledLinkContainer className={className}>
            {withPopover ? (
                <StyledPopover
                    hover
                    hoverHideDelay={500}
                    hoverShowDelay={0}
                    placement="top"
                    trigger={
                        <Button
                            iconLeft={Bell}
                            onClick={onClick}
                            size="sm"
                            variant="outlined"
                        >
                            <FormattedMessage id="manage_button_enable_notifications" />
                        </Button>
                    }
                >
                    <PopoverContentContainer>
                        <PopoverTitleText>
                            <FormattedMessage id="notifications.enable_notifications_prompt_title" />
                        </PopoverTitleText>
                        <FormattedMessage id="notifications.get_notified" />
                    </PopoverContentContainer>
                </StyledPopover>
            ) : (
                <Button
                    iconLeft={Bell}
                    onClick={onClick}
                    size="sm"
                    variant="outlined"
                >
                    <FormattedMessage id="manage_button_enable_notifications" />
                </Button>
            )}
        </StyledLinkContainer>
    );
};

export default React.memo<Props>(ManageButton);

const StyledLinkContainer = styled.div`
    display: flex;
    justify-content: flex-end;

    a {
        font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    }
`;

const StyledPopover = styled(Popover)`
    max-width: 300px;
    width: 300px;
    z-index: 13;
`;

const PopoverContentContainer = styled.div`
    padding: 15px;
`;

const PopoverTitleText = styled(H5)`
    margin-bottom: 8px;
`;
